/**
 * Workflow runs store — paginated + filterable list of executions.
 *
 * Mirrors the audit store's filter/fixture-fallback shape. Adds two
 * actions: cancel a running run, retry a failed/cancelled run.
 */
import { create } from "zustand";
import { get as apiGet, post as apiPost, ApiError } from "@/api/client";
import {
  WorkflowRunListSchema,
  type WorkflowRun,
  type WorkflowRunState,
} from "@/api/schemas/workflows";
import { MOCK_WORKFLOW_RUNS } from "@/api/fixtures/workflow-runs";

export interface WorkflowFilters {
  query: string;
  states: WorkflowRunState[];
  vendors: Array<"meraki" | "aruba" | "shasta" | "system">;
  /** Hide scheduler-triggered runs by default — they bury user activity. */
  hideScheduled: boolean;
}

const EMPTY_FILTERS: WorkflowFilters = {
  query: "",
  states: [],
  vendors: [],
  hideScheduled: false,
};

interface State {
  data: WorkflowRun[];
  total: number;
  filters: WorkflowFilters;
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;

  load: () => Promise<void>;
  setFilters: (patch: Partial<WorkflowFilters>) => void;
  resetFilters: () => void;
  cancel: (id: string) => Promise<void>;
  retry: (id: string) => Promise<void>;
}

function applyFiltersToFixture(rows: WorkflowRun[], f: WorkflowFilters): WorkflowRun[] {
  const stateSet = new Set(f.states);
  const vendorSet = new Set(f.vendors);
  return rows.filter((r) => {
    if (f.states.length > 0 && !stateSet.has(r.state)) return false;
    if (f.vendors.length > 0 && !vendorSet.has(r.vendor)) return false;
    if (f.hideScheduled && r.scheduled) return false;
    if (f.query) {
      const q = f.query.toLowerCase();
      const hay = `${r.workflowName} ${r.workflowSlug} ${r.target} ${r.triggeredBy} ${r.message ?? ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

export const useWorkflowRuns = create<State>((set, get) => ({
  data: [],
  total: 0,
  filters: EMPTY_FILTERS,
  status: "idle",
  error: null,

  load: async () => {
    if (get().status === "loading") return;
    set({ status: "loading", error: null });
    try {
      const f = get().filters;
      const raw = await apiGet<unknown>("/workflows/runs/", {
        q: f.query || undefined,
        state: f.states.length > 0 ? f.states.join(",") : undefined,
        vendor: f.vendors.length > 0 ? f.vendors.join(",") : undefined,
        hide_scheduled: f.hideScheduled ? "1" : undefined,
      });
      const parsed = WorkflowRunListSchema.parse(raw);
      set({ data: parsed, total: parsed.length, status: "ready" });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      const filtered = applyFiltersToFixture(MOCK_WORKFLOW_RUNS, get().filters);
      set({
        data: filtered,
        total: filtered.length,
        status: "error",
        error: `${message} — showing fixture data`,
      });
    }
  },

  setFilters: (patch) => {
    set((s) => ({ filters: { ...s.filters, ...patch } }));
    void get().load();
  },

  resetFilters: () => {
    set({ filters: EMPTY_FILTERS });
    void get().load();
  },

  cancel: async (id) => {
    // Optimistic — flip state immediately so the row updates without waiting.
    set((s) => ({
      data: s.data.map((r) => (r.id === id ? { ...r, state: "cancelled" } : r)),
    }));
    try {
      await apiPost(`/workflows/runs/${id}/cancel/`);
    } finally {
      void get().load();
    }
  },

  retry: async (id) => {
    set((s) => ({
      data: s.data.map((r) => (r.id === id ? { ...r, state: "queued", message: "queued for retry" } : r)),
    }));
    try {
      await apiPost(`/workflows/runs/${id}/retry/`);
    } finally {
      void get().load();
    }
  },
}));
